
import NumberInput from '@components/new-styleguide/form/NumberInput.vue';
import { Component, Prop } from 'vue-property-decorator';
import Vue from 'vue';
import { namespace } from 'vuex-class';
import { FuelStopMutation, Location, Pilot } from '@clients/myTrips';
import LocationAddress from '@/models/LocationAddress';
import PositionMap from '@components/my-trips/PositionMap.vue';
import AddressInputField from '@components/new-styleguide/form/AddressInputField.vue';
import { featureToAddress, featureToLocation } from '@util/mapbox';
import PilotSelect from '@components/new-styleguide/form/PilotSelect.vue';
import AppButton from '@components/new-styleguide/AppButton.vue';
import { formatInputDate, formatTime } from '@util/dates-and-times';
import AppContent from '@components/new-styleguide/AppContent.vue';

const TriplogModule = namespace('Triplog');
const SettingsModule = namespace('Settings');
const RouterModule = namespace('Router');
const GeographicsModule = namespace('Geographics');

@Component({
  name: 'FuelStopDetailView',
  components: {
    NumberInput,
    AppContent,
    PositionMap,
    AddressInputField,
    PilotSelect,
    AppButton,
  }
})
export default class FuelStopDetailView extends Vue {
  @Prop({ required: true })
  private value: FuelStopMutation;

  @Prop()
  private pilots: Pilot[]

  @Prop()
  private minTimestamp: string

  @Prop({ default: false })
  private loading: boolean;

  @GeographicsModule.Action('fetchPlaceNames')
  private fetchPlaceNames;

  @GeographicsModule.Getter('getPlaceNamesResponse')
  private getPlaceNamesResponse;

  @TriplogModule.Getter('getFuelUnit')
  private fuelUnit: string;

  get pilot(): Pilot {
    return this.pilots.find(p => p.id === this.value.pilotId) || null;
  }

  private handleLocationButton(location: Location) {
    this.handleLocationInput(location);
  }

  private handleAddressChange(locationAddress: LocationAddress) {
    this.input([{ key: 'address', value: { ...locationAddress.address } }, { key: 'location', value: { ...locationAddress.location } }]);
  }

  private async handleLocationInput(location: Location) {
    const locationAddress = await this.changeAddress(location);
    this.input([{ key: 'address', value: { ...locationAddress.address } }, { key: 'location', value: { ...locationAddress.location } }]);
  }

  private async changeAddress(location: Location): Promise<LocationAddress> {
    const results = await this.fetchPlaceNames({ longitude: location.longitude, latitude: location.latitude,  poi: true })
    if (results && results.features && results.features[0]) {
      return {
        address: featureToAddress(results.features[0]),
        location: featureToLocation(results.features[0])
      };
    } else {
      return null;
    }
  }

  private handleDateTimeChange(value: string, isDate: boolean) {
    if (value) {
      let newTimestamp: string = isDate ? value + 'T' + this.timeInputValue : this.dateInputValue + 'T' + value;
      this.singleInput('timestamp', new Date(newTimestamp).toISOString());
    } else {
      this.singleInput('timestamp', this.value.timestamp);
    }
  }

  get locationAddress(): LocationAddress {
    return { address: { ...this.value.address }, location: { ...this.value.location } };
  }

  get dateInputValue(): string {
    return formatInputDate(this.value.timestamp);
  }

  get timeInputValue(): string {
    return formatTime(this.value.timestamp);
  }

  get minTime(): string {
    if (this.dateInputValue === formatInputDate(this.minTimestamp)) {
      return formatTime(this.minTimestamp);
    }
  }

  get minDate(): string {
    return formatInputDate(this.minTimestamp);
  }

  get maxDate() {
    return formatInputDate(new Date());
  }

  get maxTime() {
    if (this.dateInputValue === this.maxDate) {
      return formatTime(new Date());
    }
  }

  private singleInput(name: string, value: any) {
    this.input([{ key: name, value: value }]);
  }

  private input(inputs: { key: string, value: any }[]) {
    const copy = { ...this.value };
    inputs.forEach((input) => {
      copy[input.key] = input.value;
    });
    this.$emit('input', copy);
  }
}
